import * as React from 'react';
import { StyleSheet, View, Platform } from 'react-native';
import { translate } from 'secullum-i18n';
import {
  isTablet,
  Modal,
  TextBox,
  Text,
  formatDate,
  ErrorMessage,
  Message,
  KeyboardAvoidingView,
  Header,
  StatusBar
} from 'secullum-react-native-ui';
import { ImageButton } from 'secullum-react-native-ui';
import { getTheme } from '../../shared/modules/layout';
import {
  estiloBotaoDestaque,
  estiloTextoBotaoDestaque
} from '../components/BotaoDestaque';
import { ButtonBar } from '../components/ButtonBar';
import Api from '../modules/api';
import { InconsistenciasDados } from '../modules/types';

interface Props {
  inconsistencia: InconsistenciasDados;
  onCancelar: () => void;
  onConcluir: () => void;
}

interface State {
  resultado: 'sucesso' | 'erro' | null;
  justificativa: string;
  erroJustificativa: string;
  erroGeral: string;
}

class ModalInconsistenciaJustificar extends React.Component<Props, State> {
  state: State = {
    resultado: null,
    justificativa: '',
    erroJustificativa: '',
    erroGeral: ''
  };

  handleInputChange = (name: string, value: any) => {
    this.setState({
      ...this.state,
      [name]: value,
      erroJustificativa: ''
    });
  };

  enviarInconsistenciaJustificadaAsync = (
    inconsistencia: InconsistenciasDados
  ) => {
    const api = new Api();

    if (!this.state.justificativa) {
      this.setState({ erroJustificativa: translate('Campo obrigatório.') });

      return;
    }

    inconsistencia.justificativa = this.state.justificativa;

    api.postJustificarInconsistenciaAsync(inconsistencia, {
      onSuccess: () => {
        this.setState({ resultado: 'sucesso' });
      },
      onError: erro => {
        this.setState({
          resultado: 'erro',
          erroGeral: erro.message
        });
      },
      onValidationError: listaErros => {
        let mensagemErro = '';
        listaErros.forEach(error => (mensagemErro += error.message + '\n'));

        this.setState({ resultado: 'erro', erroGeral: mensagemErro });
      }
    });
  };

  render() {
    const { inconsistencia } = this.props;
    const { erroJustificativa, erroGeral, justificativa, resultado } =
      this.state;

    const plataformaWeb = Platform.OS === 'web';

    return (
      <Modal
        visible={inconsistencia !== undefined}
        onRequestClose={this.props.onCancelar}
        overlayStyle={plataformaWeb ? styles.modalWeb : styles.modal}
      >
        <KeyboardAvoidingView
          extraWindowHeight={Header.height + StatusBar.height}
        >
          {({ availableHeight }) => (
            <View
              style={[styles.containerWrapper, { height: availableHeight }]}
            >
              <View
                style={plataformaWeb ? styles.containerWeb : styles.container}
              >
                {plataformaWeb ? (
                  <View style={styles.modalIcons}>
                    <Text bold style={styles.title}>
                      {translate('Justificativa')}
                    </Text>
                    <ImageButton
                      style={styles.imageButtonClose}
                      icon="close"
                      onPress={this.props.onCancelar}
                    />
                  </View>
                ) : (
                  <Text bold style={styles.title}>
                    {translate('Justificativa')}
                  </Text>
                )}
                <TextBox
                  nativeID="text-input-nome"
                  style={styles.bodyText}
                  value={inconsistencia.funcionarioNome}
                  label={translate('Nome do funcionário')}
                  editable={false}
                />
                <TextBox
                  nativeID="text-input-data"
                  style={styles.bodyText}
                  value={formatDate(inconsistencia.data, 'dd/MM/yyyy')}
                  label={translate('Data')}
                  editable={false}
                />
                <TextBox
                  nativeID="text-input-horas"
                  style={styles.bodyText}
                  value={inconsistencia.horas}
                  label={translate('Horas')}
                  editable={false}
                />
                <TextBox
                  nativeID="text-input-tipo"
                  style={styles.bodyText}
                  value={inconsistencia.tipo}
                  label={translate('Tipo')}
                  editable={false}
                />
                <TextBox
                  label={translate('Justificativa')}
                  style={[styles.bodyText, styles.justificativa]}
                  inputStyle={
                    plataformaWeb ? { minHeight: 60 } : { maxHeight: 60 }
                  }
                  multiline
                  maxLength={255}
                  value={justificativa}
                  onChange={texto =>
                    this.handleInputChange('justificativa', texto)
                  }
                  nativeID="text-input-justificativa"
                />
                <ErrorMessage
                  nativeID="campo-obrigatorio-justificativa"
                  message={erroJustificativa}
                  style={{ paddingHorizontal: 16 }}
                />
                <ButtonBar
                  buttonBarStyle={{ padding: 16 }}
                  leftButton={{
                    text: translate('Cancelar'),
                    onPress: this.props.onCancelar,
                    primary: false,
                    nativeID: 'botao-modal-inconsistencia-justificar-cancelar'
                  }}
                  rightButton={{
                    text: translate('Confirmar'),
                    onPress: () =>
                      this.enviarInconsistenciaJustificadaAsync(inconsistencia),
                    nativeID: 'botao-modal-inconsistencia-justificar-confirmar',
                    style: estiloBotaoDestaque,
                    textStyle: estiloTextoBotaoDestaque
                  }}
                />
                <Message
                  nativeID="inconsistencia-justificar-sucesso"
                  message={translate('Inconsistência justificada com sucesso')}
                  visible={resultado === 'sucesso'}
                  onRequestClose={() => {
                    this.setState({ resultado: null });
                    this.props.onConcluir();
                  }}
                />
                <Message
                  nativeID="inconsistencia-justificar-erro"
                  type="warning"
                  message={erroGeral}
                  visible={resultado === 'erro'}
                  onRequestClose={() =>
                    this.setState({ resultado: null, erroGeral: '' })
                  }
                />
              </View>
            </View>
          )}
        </KeyboardAvoidingView>
      </Modal>
    );
  }
}

const theme = getTheme();

const styles = StyleSheet.create({
  modal: {
    flex: 1,
    justifyContent: 'center'
  },
  modalWeb: {
    overflow: 'scroll'
  },
  modalIcons: {
    flexDirection: 'row'
  },
  containerWrapper: {
    justifyContent: 'center',
    alignItems: 'center'
  },
  container: {
    width: '90%',
    maxWidth: 366,
    backgroundColor: theme.backgroundColor1,
    borderRadius: 5
  },
  containerWeb: {
    margin: 'auto',
    width: '90%',
    maxWidth: 366,
    backgroundColor: theme.backgroundColor2,
    borderRadius: 10,
    justifyContent: 'center'
  },
  title: {
    padding: 16,
    color: theme.textColor3,
    fontSize: isTablet() ? 20 : 17
  },
  bodyText: {
    padding: 16,
    margin: 16,
    marginBottom: 5,
    marginTop: 5,
    minHeight: 60
  },
  justificativa: {
    minHeight: 100
  },
  imageButtonClose: {
    borderWidth: 0,
    marginLeft: 'auto'
  }
});

export default ModalInconsistenciaJustificar;
